import React, { useMemo, useState } from 'react';

import { AdminLedgerWorkspace } from '@/components/entertainment/AdminLedgerWorkspace';
import { ApprovalQueue } from '@/components/entertainment/ApprovalQueue';
import { BalanceHealthPanel } from '@/components/entertainment/BalanceHealthPanel';
import { EntertainmentCategoryManager } from '@/components/entertainment/EntertainmentCategoryManager';
import { EntertainmentDashboard } from '@/components/entertainment/EntertainmentDashboard';
import { PaymentAcknowledgementPanel } from '@/components/entertainment/PaymentAcknowledgementPanel';
import { Button, Card, LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useCollectionData } from '@/hooks/useCollectionData';
import {
  acknowledgePayeePayment,
  applyLedgerDraft,
  approveSharedCost,
  confirmPayerPayment,
  createPayment,
  rejectPayment,
  rejectSharedCost,
} from '@/lib/entertainment/ledger-service';
import type {
  EntertainmentActionTask,
  EntertainmentBalance,
  EntertainmentLedgerEntry,
  EntertainmentTransaction,
  LedgerDraftChange,
} from '@/lib/entertainment/ledger-types';
import { canManageEntertainment, isEligibleEntertainmentParticipant } from '@/lib/roles';
import type { EntertainmentCategory, User } from '@/lib/types';
import { formatCurrency } from '@/lib/utils';

type EntertainmentAdminView = 'management' | 'ledger' | 'categories';

const ENTERTAINMENT_ADMIN_VIEWS: Array<{ id: EntertainmentAdminView; label: string }> = [
  { id: 'management', label: 'Management' },
  { id: 'ledger', label: 'Ledger' },
  { id: 'categories', label: 'Categories' },
];

function typedTransactions(records: unknown[]): EntertainmentTransaction[] {
  return records.filter((transaction): transaction is EntertainmentTransaction => {
    const item = transaction as Partial<EntertainmentTransaction>;
    return typeof item.id === 'string'
      && typeof item.description === 'string'
      && typeof item.status === 'string'
      && typeof item.amount_cents === 'number';
  });
}

function typedEntries(records: unknown[]): EntertainmentLedgerEntry[] {
  return records.filter((entry): entry is EntertainmentLedgerEntry => {
    const item = entry as Partial<EntertainmentLedgerEntry>;
    return typeof item.id === 'string'
      && typeof item.transaction_id === 'string'
      && typeof item.user_id === 'string'
      && typeof item.amount_cents === 'number'
      && typeof item.status === 'string'
      && typeof item.entry_kind === 'string';
  });
}

function typedBalances(records: unknown[]): EntertainmentBalance[] {
  return records.filter((balance): balance is EntertainmentBalance => {
    const item = balance as Partial<EntertainmentBalance>;
    return typeof item.user_id === 'string'
      && typeof item.official_balance_cents === 'number'
      && typeof item.projected_balance_cents === 'number';
  });
}

function typedTasks(records: unknown[]): EntertainmentActionTask[] {
  return records.filter((task): task is EntertainmentActionTask => {
    const item = task as Partial<EntertainmentActionTask>;
    return item.task_type === 'entertainment_payment_acknowledgement'
      && typeof item.id === 'string'
      && typeof item.target_user_id === 'string'
      && typeof item.payment_transaction_id === 'string';
  });
}

function normalizeAdminView(value: string | null): EntertainmentAdminView {
  if (value === 'ledger' || value === 'categories') return value;
  return 'management';
}

function initialAdminView(): EntertainmentAdminView {
  if (typeof window === 'undefined') return 'management';
  return normalizeAdminView(new URLSearchParams(window.location.search).get('view'));
}

function EntertainmentSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const headingId = React.useId();

  return (
    <section
      aria-labelledby={headingId}
      className="border-b border-aura-primary/[0.06] pb-6 mb-6 last:border-b-0 last:pb-0 last:mb-0"
    >
      <h2 id={headingId} className="text-sm font-bold text-aura-primary mb-3 uppercase tracking-widest">
        {title}
      </h2>
      <div className="flex flex-col gap-4">
        {children}
      </div>
    </section>
  );
}

function EntertainmentAdminTabs({
  activeView,
  onChange,
}: {
  activeView: EntertainmentAdminView;
  onChange: (view: EntertainmentAdminView) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Entertainment admin views"
      className="mb-4 flex gap-1 overflow-x-auto rounded-xl bg-aura-primary/[0.03] border border-aura-primary/[0.08] p-1"
    >
      {ENTERTAINMENT_ADMIN_VIEWS.map((view) => {
        const active = activeView === view.id;
        return (
          <button
            key={view.id}
            id={`entertainment-tab-${view.id}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`entertainment-tabpanel-${view.id}`}
            onClick={() => onChange(view.id)}
            className={`min-h-10 min-w-max flex-1 whitespace-nowrap rounded-lg border-none px-3 text-sm font-semibold cursor-pointer transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aura-primary/35 focus-visible:ring-offset-2 ${
              active
                ? 'bg-gradient-to-br from-aura-primary to-aura-primary-dim text-on-primary shadow-[0_4px_12px_rgba(0,104,85,0.2)]'
                : 'bg-transparent text-text-muted hover:bg-aura-primary/5 hover:text-aura-primary'
            }`}
          >
            {view.label}
          </button>
        );
      })}
    </div>
  );
}

export function EntertainmentPage() {
  const { user } = useAuth();
  const [adminView, setAdminViewState] = useState<EntertainmentAdminView>(() => initialAdminView());
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const currentUserId = user?.id ?? '';
  const canManage = canManageEntertainment(user);
  const activeAdminView = canManage ? adminView : 'management';

  const { data: rawUsers, loading: usersLoading } = useCollectionData('users', 0, 'name');
  const { data: rawCategories, loading: categoriesLoading } = useCollectionData('entertainment_categories', 0, 'name');
  const {
    data: rawTransactions,
    loading: transactionsLoading,
    refresh: refreshTransactions,
  } = useCollectionData('entertainment_transactions', 0, 'updated_at');
  const {
    data: rawEntries,
    loading: entriesLoading,
    refresh: refreshEntries,
  } = useCollectionData('entertainment_ledger_entries', 0, 'created_at');
  const {
    data: rawBalances,
    loading: balancesLoading,
    refresh: refreshBalances,
  } = useCollectionData('entertainment_balances', 0, 'updated_at');
  const {
    data: rawTasks,
    loading: tasksLoading,
    refresh: refreshTasks,
  } = useCollectionData(
    'app_action_tasks',
    0,
    'updated_at',
    canManage || !currentUserId ? null : { field: 'target_user_id', op: '==', value: currentUserId },
  );

  const users = rawUsers as User[];
  const categories = rawCategories as EntertainmentCategory[];
  const transactions = useMemo(() => typedTransactions(rawTransactions), [rawTransactions]);
  const entries = useMemo(() => typedEntries(rawEntries), [rawEntries]);
  const balances = useMemo(() => typedBalances(rawBalances), [rawBalances]);
  const tasks = useMemo(() => typedTasks(rawTasks), [rawTasks]);
  const openPaymentTaskIds = useMemo(() => new Set(
    tasks
      .filter((task) => task.status === 'open')
      .map((task) => task.payment_transaction_id),
  ), [tasks]);
  const eligibleUsers = useMemo(
    () => users.filter((candidate) => isEligibleEntertainmentParticipant(candidate)),
    [users],
  );
  const userNameById = useMemo(
    () => Object.fromEntries(users.map((candidate) => [candidate.id, candidate.name])),
    [users],
  );
  const ownHistory = useMemo(() => (
    transactions.filter((transaction) =>
      transaction.originator_id === currentUserId
      || transaction.payer_id === currentUserId
      || transaction.payee_id === currentUserId
    )
  ), [currentUserId, transactions]);
  const visibleHistory = useMemo(
    () => (canManage ? transactions : ownHistory),
    [canManage, ownHistory, transactions],
  );

  const refreshLedger = async () => {
    await Promise.all([
      refreshTransactions(),
      refreshEntries(),
      refreshBalances(),
      refreshTasks(),
    ]);
  };

  const handleCreatePayment = async (body: { payer_id: string; payee_id: string; amount_cents: number }) => {
    setActionMessage(null);
    const result = await createPayment(body) as { transactionId?: string };
    if (result.transactionId) {
      await confirmPayerPayment(result.transactionId);
    }
    await refreshLedger();
    setActionMessage(result.transactionId
      ? 'Payment marked as sent. Waiting for payee acknowledgement.'
      : 'Payment created.');
  };

  const handleConfirmPayment = async (paymentId: string) => {
    setActionMessage(null);
    await confirmPayerPayment(paymentId);
    await refreshLedger();
    setActionMessage('Payment marked as sent. Waiting for payee acknowledgement.');
  };

  const handleAcknowledgePayment = async (paymentId: string) => {
    setActionMessage(null);
    await acknowledgePayeePayment(paymentId);
    await refreshLedger();
    setActionMessage('Payment acknowledged and posted.');
  };

  const handleRejectPayment = async (paymentId: string) => {
    const reason = window.prompt('Reason for payment rejection');
    if (!reason?.trim()) return;
    setActionMessage(null);
    await rejectPayment(paymentId, { reason: reason.trim() });
    await refreshLedger();
    setActionMessage('Payment rejected.');
  };

  const handleApplyLedgerDraft = async (changes: LedgerDraftChange[]) => {
    setActionMessage(null);
    await applyLedgerDraft({ changes, reason: 'Admin ledger draft review' });
    await refreshLedger();
    setActionMessage('Draft applied.');
  };

  const handleApproveTransaction = async (transactionId: string) => {
    setActionMessage(null);
    await approveSharedCost(transactionId);
    await refreshLedger();
    setActionMessage('Shared cost approved.');
  };

  const handleRejectTransaction = async (transactionId: string) => {
    const reason = window.prompt('Reason for rejection');
    if (!reason?.trim()) return;
    setActionMessage(null);
    await rejectSharedCost(transactionId, { reason: reason.trim() });
    await refreshLedger();
    setActionMessage('Shared cost rejected.');
  };

  const postedLedgerTotalCents = useMemo(() => (
    entries.filter((entry) => entry.status === 'posted').reduce((sum, entry) => sum + entry.amount_cents, 0)
  ), [entries]);
  const officialBalanceTotalCents = useMemo(() => (
    balances.reduce((sum, balance) => sum + balance.official_balance_cents, 0)
  ), [balances]);

  const setAdminView = (nextView: EntertainmentAdminView) => {
    setAdminViewState(nextView);
    if (typeof window === 'undefined') return;

    const nextUrl = new URL(window.location.href);
    if (nextView === 'management') {
      nextUrl.searchParams.delete('view');
    } else {
      nextUrl.searchParams.set('view', nextView);
    }
    window.history.replaceState(null, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  };

  if (
    usersLoading
    || categoriesLoading
    || transactionsLoading
    || entriesLoading
    || balancesLoading
    || tasksLoading
  ) {
    return <LoadingSpinner message="Loading Entertainment Ledger..." />;
  }

  return (
    <div className="max-w-md mx-auto py-6 px-4 space-y-6 min-h-screen bg-[#f5f7f8]">
      <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5">
        <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">ENTERTAINMENT</div>
        <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight">Entertainment Ledger</h1>
      </div>

      {actionMessage && (
        <div role="status" className="bg-status-success/10 text-status-success p-3 rounded-lg mb-4 text-center text-sm font-semibold">
          {actionMessage}
        </div>
      )}

      {canManage && (
        <EntertainmentAdminTabs activeView={activeAdminView} onChange={setAdminView} />
      )}

      {activeAdminView === 'management' && (
        <div
          id="entertainment-tabpanel-management"
          role={canManage ? 'tabpanel' : undefined}
          aria-labelledby={canManage ? 'entertainment-tab-management' : undefined}
          className="flex flex-col gap-6"
        >
          {canManage && (
            <BalanceHealthPanel
              ledgerTotalCents={postedLedgerTotalCents}
              materializedDriftCents={officialBalanceTotalCents - postedLedgerTotalCents}
            />
          )}

          {canManage && (
            <EntertainmentSection title="Admin Controls">
              <ApprovalQueue transactions={transactions} onApprove={handleApproveTransaction} onReject={handleRejectTransaction} />
            </EntertainmentSection>
          )}

          <EntertainmentSection title="Action Queue">
            <PaymentAcknowledgementPanel
              currentUserId={currentUserId}
              canAcknowledgeAny={canManage}
              tasks={tasks}
              userNames={userNameById}
              onAcknowledge={handleAcknowledgePayment}
              onReject={handleRejectPayment}
            />
          </EntertainmentSection>

          <EntertainmentSection title="Balances">
            <EntertainmentDashboard
              balances={balances}
              users={users}
              currentUserId={currentUserId}
              canManage={canManage}
              categories={categories}
              eligibleUsers={eligibleUsers}
              onCreatePayment={handleCreatePayment}
              onSharedCostSubmitted={refreshLedger}
            />
          </EntertainmentSection>

          <EntertainmentSection title="History">
            <Card className="p-4">
              <div className="flex flex-col gap-3">
                <h2 className="text-sm font-bold text-text-muted mb-3 uppercase tracking-widest">
                  {canManage ? 'All History' : 'My History'}
                </h2>
                {visibleHistory.length === 0 ? (
                  <div className="text-sm text-text-muted">No entertainment transactions yet.</div>
                ) : (
                  visibleHistory.map((transaction) => {
                    const payerName = transaction.payer_id ? userNameById[transaction.payer_id] ?? transaction.payer_id : 'Unknown payer';
                    const payeeName = transaction.payee_id ? userNameById[transaction.payee_id] ?? transaction.payee_id : 'Unknown payee';
                    const paymentActionLabel = `payment from ${payerName} to ${payeeName}`;
                    const canConfirmPayment = transaction.kind === 'payment'
                      && transaction.status === 'pending'
                      && (canManage || transaction.payer_id === currentUserId);
                    const canAdminAcknowledgePayment = canManage
                      && transaction.kind === 'payment'
                      && transaction.status === 'payer_confirmed'
                      && !openPaymentTaskIds.has(transaction.id);
                    const canAdminRejectPayment = canManage
                      && transaction.kind === 'payment'
                      && (transaction.status === 'pending' || transaction.status === 'payer_confirmed');

                    return (
                      <div key={transaction.id} className="flex items-center justify-between gap-3 rounded-md border border-border-subtle p-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-text-main">{transaction.description}</div>
                          <div className="text-xs text-text-muted">{transaction.status}</div>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                          <span className="text-sm font-bold text-text-main">{formatCurrency(transaction.amount_cents / 100)}</span>
                          {canConfirmPayment && (
                            <Button type="button" size="sm" onClick={() => handleConfirmPayment(transaction.id)}>
                              Confirm Paid
                            </Button>
                          )}
                          {canAdminAcknowledgePayment && (
                            <Button
                              type="button"
                              size="sm"
                              aria-label={`Acknowledge ${paymentActionLabel}`}
                              onClick={() => handleAcknowledgePayment(transaction.id)}
                            >
                              Acknowledge
                            </Button>
                          )}
                          {canAdminRejectPayment && (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              aria-label={`Reject ${paymentActionLabel}`}
                              onClick={() => handleRejectPayment(transaction.id)}
                            >
                              Reject
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </EntertainmentSection>
        </div>
      )}

      {canManage && activeAdminView === 'ledger' && (
        <div
          id="entertainment-tabpanel-ledger"
          role="tabpanel"
          aria-labelledby="entertainment-tab-ledger"
          className="flex flex-col gap-4"
        >
          <AdminLedgerWorkspace entries={entries} users={users} onApply={handleApplyLedgerDraft} />
        </div>
      )}

      {canManage && activeAdminView === 'categories' && (
        <div
          id="entertainment-tabpanel-categories"
          role="tabpanel"
          aria-labelledby="entertainment-tab-categories"
          className="flex flex-col gap-4"
        >
          <EntertainmentCategoryManager users={users} categories={categories} />
        </div>
      )}
    </div>
  );
}
